import chroma from 'chroma-js';
/**
 * Chart styling — derived from the central design system.
 *
 * The first series is always the brand color so charts stay visually
 * coherent with the rest of the app. Remaining series rotate evenly
 * around the brand hue using OKLCH for perceptual uniformity.
 */

import { TenantBranding } from '../hooks/useBranding';
import { buildTokens, Mode } from './designSystem';

/**
 * Returns the categorical chart palette to use.
 * Resolution order:
 *   1. Tenant `branding.colors.charts` (explicit override)
 *   2. Brand-derived rotation around the tenant primary
 *   3. Fallback brand-derived palette (no tenant data)
 */
export function getChartPalette(branding?: TenantBranding | null): string[] {
  if (branding?.colors?.charts && branding.colors.charts.length > 0) {
    return branding.colors.charts;
  }
  return buildTokens('light', branding).chart.palette;
}

/**
 * The chart palette without the hues that read as danger — reds and
 * near-reds — for telling groups apart (a stage, a team) by colour where a
 * red would be read as "something is wrong". Charts keep the full palette.
 * Hues are measured, not assumed: the palette turns with the brand colour.
 */
export function identityPalette(palette: string[]): string[] {
  const calm = palette.filter((hex) => {
    const [h, c] = chroma(hex).hcl();
    // In this colour space red sits near 40° and pink near 0°; orange starts about 50°.
    return Number.isNaN(h) || c < 25 || (h > 47 && h < 345);
  });
  return calm.length ? calm : palette;
}

// Static fallback (matches backend/constants.py for backwards compatibility
// with any code that doesn't yet pass branding).
export const CHART_PALETTE: string[] = buildTokens('light', null).chart.palette;

// Default neutrals — exposed for non-themed Vega-Lite renderer fallbacks.
const DEFAULTS = buildTokens('light', null);
export const CHART_BACKGROUND = 'transparent';
export const CHART_AXIS_COLOR = DEFAULTS.chart.axis;
export const CHART_AXIS_LINE_COLOR = DEFAULTS.chart.axisLine;
export const CHART_GRID_COLOR = DEFAULTS.chart.grid;
export const CHART_LEGEND_TEXT_COLOR = DEFAULTS.chart.legend;

// Recharts tooltip style — uses tokens from the design system
export const RECHARTS_TOOLTIP_STYLE = {
  background: DEFAULTS.chart.tooltipBg,
  border: `1px solid ${DEFAULTS.chart.tooltipBorder}`,
  borderRadius: 10,
  boxShadow: DEFAULTS.shadow.md,
} as const;

// ---------------------------------------------------------------------------
// ECharts theme — registered via echarts.registerTheme('klaris', …)
// ---------------------------------------------------------------------------
// `mode` is required so legend/axis/grid colors and pie slice borders adapt
// to the current UI palette. Earlier this hard-coded 'light', which produced
// near-invisible (and therefore "blurry-looking") labels in dark mode.
export function buildEChartsTheme(mode: Mode, branding?: TenantBranding | null) {
  const t = buildTokens(mode, branding);
  const palette = getChartPalette(branding);
  // Pie slices render a thin gap between segments. Stroke color must match
  // the surface the chart sits on (cards = `surface.raised`) so the gap reads
  // as a separator rather than a halo on dark backgrounds.
  const sliceBorder = t.surface.raised;
  return {
    color: palette,
    backgroundColor: 'transparent',
    textStyle: {
      // Single quotes deliberately. The SVG renderer writes this straight into
      // a double-quoted style="" attribute without escaping, so double quotes
      // here produce an SVG export that no XML parser will open.
      fontFamily: "'Inter', 'Roboto', 'Helvetica Neue', sans-serif",
    },
    title: {
      textStyle: { fontSize: 14, fontWeight: 600, color: t.chart.legend },
      subtextStyle: { fontSize: 12, color: t.chart.axis },
    },
    legend: {
      textStyle: { fontSize: 12, color: t.chart.legend },
      pageTextStyle: { color: t.chart.axis },
      icon: 'circle',
      itemWidth: 10,
      itemHeight: 10,
      itemGap: 16,
    },
    tooltip: {
      backgroundColor: t.chart.tooltipBg,
      borderColor: t.chart.tooltipBorder,
      borderWidth: 1,
      padding: [10, 14],
      textStyle: { fontSize: 13, color: t.text.primary, lineHeight: 20 },
      extraCssText: `border-radius:10px;box-shadow:${t.shadow.md};`,
    },
    categoryAxis: {
      axisLine: { lineStyle: { color: t.chart.axisLine } },
      axisTick: { show: false },
      axisLabel: { color: t.chart.axis, fontSize: 12 },
      nameTextStyle: { color: t.chart.axis, fontSize: 12, fontWeight: 600 },
      splitLine: { lineStyle: { color: t.chart.grid, type: 'solid' as const, opacity: 0.6 } },
    },
    valueAxis: {
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: t.chart.axis, fontSize: 12 },
      nameTextStyle: { color: t.chart.axis, fontSize: 12, fontWeight: 600 },
      splitLine: { lineStyle: { color: t.chart.grid, type: 'solid' as const, opacity: 0.8 } },
    },
    line: {
      smooth: false,
      symbol: 'circle',
      symbolSize: 7,
      lineStyle: { width: 2.5 },
    },
    bar: {
      itemStyle: { borderRadius: [3, 3, 0, 0] },
      barMaxWidth: 64,
    },
    pie: {
      itemStyle: { borderColor: sliceBorder, borderWidth: 2 },
      // Pie slice labels (e.g. "chatbot: 17.51%") and their leader lines must
      // explicitly use the legend / axis tokens — otherwise ECharts falls back
      // to its built-in dark text, which is unreadable on dark canvases.
      label: { color: t.chart.legend, fontSize: 12 },
      labelLine: { lineStyle: { color: t.chart.axisLine } },
    },
    scatter: {
      symbolSize: 10,
    },
    emphasis: {
      focus: 'series',
      blurScope: 'coordinateSystem',
    },
  };
}
